import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../config';

const RESET_PHRASE = 'ERASE ALL PIPELINE DATA';

const buildTemplate = (table) => {
  if (!table?.columns) {
    return {};
  }
  const template = {};
  table.columns.forEach((column) => {
    template[column.name] = column.name === table.primary_key ? '' : '';
  });
  return template;
};

function DatabaseInspector() {
  const [tables, setTables] = useState([]);
  const [tablesError, setTablesError] = useState(null);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [selectedName, setSelectedName] = useState('');

  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState(null);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [editorJson, setEditorJson] = useState('');
  const [editorMode, setEditorMode] = useState('create');
  const [editorPk, setEditorPk] = useState(null);
  const [editorError, setEditorError] = useState(null);
  const [editorSuccess, setEditorSuccess] = useState(null);
  const [editorSaving, setEditorSaving] = useState(false);

  const [clearPhrase, setClearPhrase] = useState('');
  const [clearFeedback, setClearFeedback] = useState(null);
  const [clearLoading, setClearLoading] = useState(false);

  const [resetPhrase, setResetPhrase] = useState('');
  const [resetFeedback, setResetFeedback] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const selectedTable = useMemo(
    () => tables.find((t) => t.name === selectedName) || null,
    [tables, selectedName]
  );

  const totalPages = useMemo(() => (
    rowsTotal === 0 ? 1 : Math.max(1, Math.ceil(rowsTotal / perPage))
  ), [rowsTotal, perPage]);

  const fetchTables = async () => {
    setTablesLoading(true);
    setTablesError(null);
    try {
      const { data } = await axios.get(`${config.apiBaseUrl}/admin/db/tables`);
      const list = data.tables || [];
      setTables(list);
      if (!selectedName && list.length > 0) {
        setSelectedName(list[0].name);
      }
    } catch (error) {
      console.error('Failed to load table metadata', error);
      setTablesError(error.response?.data?.error || 'Unable to load table list');
    } finally {
      setTablesLoading(false);
    }
  };

  const fetchRows = async (tableName = selectedName, nextPage = page, nextPerPage = perPage) => {
    if (!tableName) return;
    setRowsLoading(true);
    setRowsError(null);
    try {
      const { data } = await axios.get(`${config.apiBaseUrl}/admin/db/table/${tableName}`, {
        params: { page: nextPage, per_page: nextPerPage }
      });
      setRows(data.rows || []);
      setRowsTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load rows', error);
      setRowsError(error.response?.data?.error || 'Unable to load rows');
    } finally {
      setRowsLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedTable) return;
    setPage(1);
    setEditorMode('create');
    setEditorPk(null);
    setEditorJson(JSON.stringify(buildTemplate(selectedTable), null, 2));
    fetchRows(selectedTable.name, 1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable?.name]);

  useEffect(() => {
    if (!selectedTable) return;
    fetchRows(selectedTable.name, page, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const refreshAll = async () => {
    await Promise.all([fetchTables(), fetchRows()]);
  };

  const startEditing = (row) => {
    setEditorMode('edit');
    setEditorPk(row?.[selectedTable?.primary_key]);
    setEditorJson(JSON.stringify(row, null, 2));
    setEditorError(null);
    setEditorSuccess(null);
  };

  const startCreate = () => {
    setEditorMode('create');
    setEditorPk(null);
    setEditorJson(JSON.stringify(buildTemplate(selectedTable), null, 2));
    setEditorError(null);
    setEditorSuccess(null);
  };

  const persistRow = async () => {
    if (!selectedTable) return;
    setEditorSaving(true);
    setEditorError(null);
    setEditorSuccess(null);
    try {
      const parsed = JSON.parse(editorJson || '{}');
      if (editorMode === 'edit' && (editorPk === undefined || editorPk === null)) {
        throw new Error('No primary key selected for edit');
      }
      if (editorMode === 'edit') {
        await axios.put(
          `${config.apiBaseUrl}/admin/db/table/${selectedTable.name}/${editorPk}`,
          parsed
        );
        setEditorSuccess('Row updated');
      } else {
        await axios.post(
          `${config.apiBaseUrl}/admin/db/table/${selectedTable.name}`,
          parsed
        );
        setEditorSuccess('Row inserted');
      }
      await refreshAll();
    } catch (error) {
      console.error('Failed to save row', error);
      if (error instanceof SyntaxError) {
        setEditorError('Invalid JSON payload');
      } else {
        setEditorError(error.response?.data?.error || error.message || 'Save failed');
      }
    } finally {
      setEditorSaving(false);
    }
  };

  const deleteRow = async (row) => {
    if (!selectedTable) return;
    const pk = row?.[selectedTable.primary_key];
    if (pk === undefined || pk === null) return;
    
    // If deleting from raw_patents, check for related records first
    if (selectedTable.name === 'raw_patents') {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/admin/db/table/${selectedTable.name}/${pk}/related`);
        const { related_records, total_related } = response.data;
        
        if (total_related > 0) {
          // Build detailed confirmation message
          const relatedInfo = Object.entries(related_records)
            .map(([table, count]) => `  • ${count} record${count > 1 ? 's' : ''} from ${table}`)
            .join('\n');
          
          const confirmMessage = `Delete patent ${pk}?\n\nThis will also delete:\n${relatedInfo}\n\nTotal: ${total_related + 1} record${total_related + 1 > 1 ? 's' : ''} will be deleted.`;
          
          if (!window.confirm(confirmMessage)) return;
        } else {
          if (!window.confirm(`Delete patent ${pk}? (No related records found)`)) return;
        }
      } catch (error) {
        console.error('Failed to check related records', error);
        // Fall back to simple confirmation
        if (!window.confirm(`Delete this row permanently? (Could not check related records)`)) return;
      }
    } else {
      if (!window.confirm('Delete this row permanently?')) return;
    }
    
    try {
      await axios.delete(`${config.apiBaseUrl}/admin/db/table/${selectedTable.name}/${pk}`);
      await refreshAll();
    } catch (error) {
      console.error('Failed to delete row', error);
      setRowsError(error.response?.data?.error || 'Delete failed');
    }
  };

  const handleClearTable = async () => {
    if (!selectedTable) return;
    const expected = `ERASE ${selectedTable.name.toUpperCase()}`;
    if (clearPhrase !== expected) {
      setClearFeedback(`Type “${expected}” exactly to clear this table.`);
      return;
    }
    setClearLoading(true);
    setClearFeedback(null);
    try {
      await axios.post(`${config.apiBaseUrl}/admin/db/table/${selectedTable.name}/truncate`, {
        confirm_phrase: clearPhrase || ''
      });
      setClearFeedback('Table cleared');
      setClearPhrase('');
      await refreshAll();
    } catch (error) {
      console.error('Failed to clear table', error);
      setClearFeedback(error.response?.data?.error || 'Unable to clear table');
    } finally {
      setClearLoading(false);
    }
  };

  const handleResetDatabase = async () => {
    if (resetPhrase !== RESET_PHRASE) {
      setResetFeedback(`Type “${RESET_PHRASE}” exactly to reset the database.`);
      return;
    }
    setResetLoading(true);
    setResetFeedback(null);
    try {
      await axios.post(`${config.apiBaseUrl}/admin/db/reset`, {
        confirm_phrase: resetPhrase || ''
      });
      setResetFeedback('Database cleared. All tables are now empty.');
      setResetPhrase('');
      await refreshAll();
    } catch (error) {
      console.error('Failed to reset database', error);
      setResetFeedback(error.response?.data?.error || 'Unable to reset database');
    } finally {
      setResetLoading(false);
    }
  };

  const renderCellValue = (value) => {
    if (value === null || value === undefined) return { display: '—', full: '—' };
    
    let fullText;
    if (typeof value === 'object') {
      try {
        fullText = JSON.stringify(value, null, 2);
      } catch (err) {
        fullText = String(value);
      }
    } else {
      fullText = String(value);
    }
    
    // Truncate display if longer than 100 characters
    const maxDisplayLength = 100;
    const displayText = fullText.length > maxDisplayLength 
      ? fullText.substring(0, maxDisplayLength) + '…'
      : fullText;
    
    return { display: displayText, full: fullText };
  };

  return (
    <div className="db-admin">
      <div className="db-admin-header">
        <div>
          <label>Table</label>
          <select
            value={selectedName}
            onChange={(e) => setSelectedName(e.target.value)}
            disabled={tablesLoading || tables.length === 0}
          >
            {tables.map((table) => (
              <option key={table.name} value={table.name}>
                {table.label} ({table.row_count} rows)
              </option>
            ))}
          </select>
          {tablesError && <p className="inline-error compact">{tablesError}</p>}
        </div>
        {selectedTable && (
          <div className="db-table-meta">
            <div>
              <span>Primary key</span>
              <strong>{selectedTable.primary_key}</strong>
            </div>
            <div>
              <span>Columns</span>
              <strong>{selectedTable.columns.length}</strong>
            </div>
            <div>
              <span>Total rows</span>
              <strong>{rowsTotal}</strong>
            </div>
          </div>
        )}
      </div>

      {selectedTable ? (
        <>
          <div className="db-body">
            <div className="db-rows">
              <div className="db-rows-header">
                <div>
                  <h3>Rows</h3>
                  {rowsError && <p className="inline-error compact">{rowsError}</p>}
                </div>
                <label>
                  Rows per page
                  <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                    {[10, 25, 50, 100].map((count) => (
                      <option key={count} value={count}>{count}</option>
                    ))}
                  </select>
                </label>
              </div>

              {rowsLoading ? (
                <p className="muted">Loading rows…</p>
              ) : rows.length === 0 ? (
                <p className="muted">No rows found.</p>
              ) : (
                <div className="db-table-wrapper">
                  <table className="db-table">
                    <thead>
                      <tr>
                        {selectedTable.columns.map((col) => (
                          <th key={col.name}>{col.name}</th>
                        ))}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => (
                        <tr key={row[selectedTable.primary_key] ?? `row-${idx}`}>
                          {selectedTable.columns.map((col) => {
                            const cellData = renderCellValue(row[col.name]);
                            return (
                              <td key={col.name} title={cellData.full}>
                                <code className="db-cell-content">{cellData.display}</code>
                              </td>
                            );
                          })}
                          <td className="db-actions">
                            <button
                              className="btn btn-secondary btn-small"
                              onClick={() => startEditing(row)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => deleteRow(row)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="db-pagination">
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </button>
                <span>Page {page} of {totalPages}</span>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="db-editor">
              <div className="db-editor-header">
                <h3>{editorMode === 'edit' ? 'Edit row' : 'Insert row'}</h3>
                {editorMode === 'edit' && (
                  <button className="btn btn-secondary btn-small" onClick={startCreate}>
                    New row
                  </button>
                )}
              </div>
              <textarea
                value={editorJson}
                onChange={(e) => setEditorJson(e.target.value)}
                rows={18}
                spellCheck={false}
              />
              <button
                className="btn btn-primary"
                onClick={persistRow}
                disabled={editorSaving}
              >
                {editorSaving ? 'Saving…' : 'Save row'}
              </button>
              {editorSuccess && <p className="inline-success compact">{editorSuccess}</p>}
              {editorError && <p className="inline-error compact">{editorError}</p>}
            </div>
          </div>

          <div className="db-controls">
            <div className="db-clear-panel">
              <h4>Clear selected table</h4>
              <p>Type “ERASE {selectedTable.name.toUpperCase()}” to enable clearing every row in this table.</p>
              <input
                type="text"
                value={clearPhrase}
                onChange={(e) => setClearPhrase(e.target.value)}
                placeholder={`ERASE ${selectedTable.name.toUpperCase()}`}
              />
              <button
                className="btn btn-danger"
                onClick={handleClearTable}
                disabled={clearLoading}
              >
                {clearLoading ? 'Clearing…' : 'Clear table'}
              </button>
              {clearFeedback && <p className="hint">{clearFeedback}</p>}
            </div>

            <div className="db-clear-panel danger">
              <h4>Reset entire database</h4>
              <p>Type “{RESET_PHRASE}” to wipe every managed table. This cannot be undone.</p>
              <input
                type="text"
                value={resetPhrase}
                onChange={(e) => setResetPhrase(e.target.value)}
                placeholder={RESET_PHRASE}
              />
              <button
                className="btn btn-danger"
                onClick={handleResetDatabase}
                disabled={resetLoading}
              >
                {resetLoading ? 'Resetting…' : 'Reset database'}
              </button>
              {resetFeedback && <p className="hint">{resetFeedback}</p>}
            </div>
          </div>
        </>
      ) : (
        <p className="muted">No tables available.</p>
      )}
    </div>
  );
}

export default DatabaseInspector;
